/**
 * O vocabulário das séries de estocagem, num lugar só.
 *
 * Existe porque duas telas precisam dizer a MESMA coisa sobre o mesmo dado: a
 * ficha de um material (`telas/Substrato.jsx`) e a tela da evidência inteira
 * (`telas/Estocagem.jsx`). Quando o rótulo do eixo e a regra de qual série é a
 * referência moram dentro de cada tela, as duas divergem no primeiro conserto
 * feito só em uma delas — e divergir aqui não é feio, é mentira: a mesma
 * medição apareceria com dois rótulos diferentes.
 *
 * O eixo é o conceito mais perigoso do catálogo. A MESMA estocagem **sobe** o
 * valor no eixo "por kg de SV" e **desce** no eixo "por tonelada de material",
 * porque o SV é o denominador e ele cai mais rápido que o metano que resta.
 * Uma linha sem o eixo declarado não é um dado incompleto: é uma armadilha.
 */

/**
 * Rótulo curto do eixo, para caber na frase.
 *
 * "por tonelada de material" encurta para "por tonelada" porque a frase já
 * fala de material; "por kg de SV" não encurta porque não há o que cortar sem
 * perder o SV, que é justamente o que explica a inversão de sinal.
 */
export const EIXO_CURTO = {
  'por kg de SV': 'por kg de SV',
  'por tonelada de material': 'por tonelada',
};

/** O eixo em forma curta, com o valor cru como reserva se vier eixo novo. */
export function eixoCurto(eixo) {
  return EIXO_CURTO[eixo] ?? eixo;
}

/** True quando o eixo mede massa — o que o produtor bombeia. */
export function eixoPorMassa(eixo) {
  return eixo === 'por tonelada de material';
}

/**
 * A série de onde a perda de referência do item saiu.
 *
 * A API publica a POSIÇÃO da série (`perda_se_estocar_indice`) em vez de
 * copiar os campos dela, e esta função é a única que resolve esse ponteiro.
 * Reencontrar a série comparando o percentual publicado com o de cada série
 * seria frágil de duas formas: igualdade de float, e empate entre séries de
 * mesma perda em eixos diferentes — que é exatamente o caso de
 * `bovino_corte_confinado`, onde 85% aparece em duas leituras.
 */
export function serieDeReferencia(item) {
  const series = item?.estocagem ?? [];
  const i = item?.perda_se_estocar_indice;
  if (i == null) return null;
  return series[i] ?? null;
}

/**
 * A série de maior perda da ficha, quando ela NÃO é a de referência por estar
 * no outro eixo. Devolve `null` quando não há o que explicar.
 *
 * Isto existe por causa de um defeito de leitura real: `bovino_corte_confinado`
 * imprime 94% numa série e diz que a referência do app é 85%. Escolher o
 * número menor, numa tela cuja regra é que o padrão deve errar na direção que
 * NÃO lisonjeia, pede explicação — e a explicação é o eixo: 94% é perda de
 * concentração (por kg de SV), 85% é perda de massa (por tonelada), e é a
 * massa que o produtor bombeia.
 *
 * A exigência de eixo diferente não é decoração. Se a maior perda estivesse no
 * MESMO eixo da referência, o eixo não explicaria nada e a frase culparia o
 * eixo por uma escolha que ele não fez. O motor garante que isso não acontece
 * (`test_a_serie_de_referencia_e_a_maior_do_proprio_eixo`); aqui a condição
 * fica escrita para que, se a garantia cair, a tela cale em vez de inventar.
 */
export function maiorPerdaEmOutroEixo(item) {
  const series = item?.estocagem ?? [];
  const referencia = serieDeReferencia(item);
  if (referencia == null || !series.length) return null;

  const maior = series.reduce((a, b) => (b.perda_pct > a.perda_pct ? b : a), series[0]);
  if (maior.perda_pct <= referencia.perda_pct) return null;
  if (maior.eixo === referencia.eixo) return null;
  return maior;
}

/**
 * True quando o rótulo de destino da série já diz o prazo, e repetir "em N
 * dias" na frase vira eco.
 *
 * Nasceu de uma frase real: `suino_terminacao` imprimia «de "fresco,
 * recém-coletado" para "90 dias a 25 °C" em 90 dias» — o 90 duas vezes na
 * mesma oração. Não é só feio: numa tela que pede para o leitor conferir
 * número por número, repetição gratuita ensina a passar o olho.
 *
 * A checagem é pelo número, não pela palavra "dias", porque o rótulo pode
 * dizer "8 meses" (e aí o prazo em dias ACRESCENTA: 240) ou "90 dias a 25 °C"
 * (e aí não acrescenta nada). O limite de palavra evita que 90 case com 190.
 */
export function diasJaNoRotulo(serie) {
  if (serie?.dias == null || !serie?.para) return false;
  return new RegExp(`(^|[^0-9])${serie.dias}([^0-9]|$)`).test(serie.para);
}

/**
 * A identidade de uma MEDIÇÃO, que não é a mesma coisa que uma linha da lista.
 *
 * `/v1/substratos/estocagem` devolve 21 linhas e diz, no mesmo corpo, que há
 * 11 séries. Não há contradição: a mesma medição alemã de chorume serve três
 * categorias do catálogo (terminação, crescimento, matriz UPL), porque o
 * dejeto é o mesmo e o estudo não separou. Contar 21 inflaria a base de
 * evidência em quase o dobro — e inflar base de evidência é o defeito que
 * este app existe para não cometer.
 *
 * O eixo entra na chave de propósito: o MESMO frasco medido por kg de SV e
 * por tonelada são duas medições, com sinais opostos. Fundi-las esconderia
 * exatamente a armadilha que a tela precisa mostrar.
 */
export function chaveDaMedicao(efeito) {
  return [efeito?.fonte, efeito?.de, efeito?.para, efeito?.eixo].join(' ◆ ');
}

/**
 * As 21 linhas viram as medições distintas, cada uma sabendo quem a reusa.
 *
 * A ordem de chegada é preservada em vez de ordenada por perda: a API entrega
 * as séries de cada categoria juntas, e essa ordem agrupa por estudo quase de
 * graça. Ordenar por tamanho de perda daria uma lista mais impressionante e
 * embaralharia os pares do mesmo ensaio, que é o que torna o eixo legível.
 */
export function medicoesDistintas(series) {
  const porChave = new Map();
  for (const linha of series ?? []) {
    const chave = chaveDaMedicao(linha.efeito);
    if (!porChave.has(chave)) {
      porChave.set(chave, { chave, efeito: linha.efeito, usos: [] });
    }
    porChave.get(chave).usos.push({
      chave: linha.substrato,
      rotulo: linha.rotulo,
      especie: linha.especie,
    });
  }
  return [...porChave.values()];
}

/** As medições agrupadas pelo estudo que as publicou, na ordem de chegada. */
export function porEstudo(medicoes) {
  const grupos = new Map();
  for (const m of medicoes) {
    const fonte = m.efeito?.fonte ?? '';
    if (!grupos.has(fonte)) grupos.set(fonte, []);
    grupos.get(fonte).push(m);
  }
  return [...grupos.entries()];
}

/**
 * A mesma medição lida no outro eixo, quando ela existe.
 *
 * É o antídoto da armadilha. A série que "ganha" 9,6% guardada a 5 °C é o
 * MESMO frasco que perde 11,6% por tonelada: o dejeto não melhorou, o
 * denominador encolheu. Publicar a primeira sozinha ensina o produtor a
 * guardar dejeto, que é o contrário do que ele deve fazer — e nenhuma
 * ressalva em prosa convence tanto quanto os dois números lado a lado.
 *
 * Casa por (fonte, de, para) com eixo diferente, nunca por percentual: dois
 * ensaios distintos podem cair o mesmo tanto, e um par falso aqui afirmaria
 * que o mesmo frasco foi lido duas vezes quando não foi.
 */
export function parNoOutroEixo(medicao, medicoes) {
  const e = medicao?.efeito;
  if (!e) return null;
  return (
    medicoes.find(
      (m) =>
        m !== medicao &&
        m.efeito.fonte === e.fonte &&
        m.efeito.de === e.de &&
        m.efeito.para === e.para &&
        m.efeito.eixo !== e.eixo,
    ) ?? null
  );
}

/**
 * True quando a série é um ganho aparente — valor que SOBE com a estocagem.
 *
 * Separado de `eixo_engana` porque são afirmações diferentes: o campo da API
 * diz "este eixo engana quem lê sem contexto", e esta função diz "o número
 * tem sinal invertido". Hoje coincidem nas três linhas que existem; no dia em
 * que uma perda pequena for marcada como enganosa sem ser negativa, a tela
 * não pode chamá-la de ganho.
 */
export function ganhoAparente(efeito) {
  return (efeito?.perda_pct ?? 0) < 0;
}
